import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { orderRiskSchema } from "./fraud.js";
import { stockQtySchema } from "./inventory.js";
import { paisa } from "./money.js";
import { paymentMethodSchema } from "./payments.js";
import { businessPhoneSchema, taxModeSchema } from "./settings.js";
/** Orders, their lines and timeline (docs/specs/sales.md, PRD §9.1). */
export const ORDER_STATUSES = [
    "draft",
    "pending",
    "confirmed",
    "packed",
    "shipped",
    "delivered",
    "partially_delivered",
    "returned",
    "cancelled",
];
export const orderStatusSchema = z.enum(ORDER_STATUSES);
/** Orders that count as sales in summaries and reports: confirmed at some point (docs/specs/reporting.md). */
export const SALE_STATUSES = [
    "confirmed",
    "packed",
    "shipped",
    "delivered",
    "partially_delivered",
    "returned",
];
export const PAYMENT_STATUSES = ["unpaid", "partially_paid", "paid", "cod_pending", "refunded"];
export const paymentStatusSchema = z.enum(PAYMENT_STATUSES);
export const ORDER_CHANNELS = ["pos", "manual", "facebook", "whatsapp", "phone", "storefront"];
export const orderChannelSchema = z.enum(ORDER_CHANNELS);
/** Channels an Admin user may create an order for; POS sales come through sync, storefront orders later. */
export const MANUAL_ORDER_CHANNELS = ["manual", "facebook", "whatsapp", "phone"];
export const ORDER_EVENT_KINDS = ["status", "payment"];
export const MAX_ORDER_LINES = 200;
export const discountSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("amount"), paisa: paisa().min(0) }).strict(),
    z.object({ kind: z.literal("percent"), bp: z.int().min(0).max(10_000) }).strict(),
]);
export const orderLineInputSchema = z
    .object({
    variantId: z.uuid(),
    qty: stockQtySchema.refine((value) => BigInt(value) > 0n, "Must be more than zero"),
    unitPricePaisa: paisa().min(0).optional(),
    discount: discountSchema.optional(),
})
    .strict();
export const orderLinesInputSchema = z
    .array(orderLineInputSchema)
    .min(1)
    .max(MAX_ORDER_LINES)
    .refine((lines) => new Set(lines.map((line) => line.variantId.toLowerCase())).size === lines.length, "Each variant once");
const positiveQty = stockQtySchema.refine((value) => BigInt(value) > 0n, "Must be more than zero");
const note = z.string().trim().max(500);
const version = z.int().min(1);
/** Where the order goes (O-01): checked against the geo reference data; kept as a snapshot on the order. */
export const shippingAddressInputSchema = z
    .object({
    recipientName: z.string().trim().max(200).default(""),
    phone: businessPhoneSchema,
    divisionId: z.int().min(1).nullable().default(null),
    districtId: z.int().min(1).nullable().default(null),
    upazilaId: z.int().min(1).nullable().default(null),
    line: z.string().trim().min(1).max(300),
})
    .strict();
export const shippingAddressSchema = z.object({
    recipientName: z.string(),
    phone: z.string(),
    divisionId: z.int().nullable(),
    districtId: z.int().nullable(),
    upazilaId: z.int().nullable(),
    divisionName: z.string().nullable(),
    districtName: z.string().nullable(),
    upazilaName: z.string().nullable(),
    line: z.string(),
});
export const orderSchema = z.object({
    id: z.uuid(),
    number: z.string(),
    channel: orderChannelSchema,
    status: orderStatusSchema,
    paymentStatus: paymentStatusSchema,
    locationId: z.uuid(),
    customerId: z.uuid().nullable(),
    deviceId: z.uuid().nullable(),
    shiftId: z.uuid().nullable(),
    clientMutationId: z.uuid().nullable(),
    taxMode: taxModeSchema,
    subtotalPaisa: paisa(),
    lineDiscountsPaisa: paisa(),
    billDiscountPaisa: paisa(),
    taxPaisa: paisa(),
    deliveryChargePaisa: paisa(),
    totalPaisa: paisa(),
    /** The delivery charge was typed, not the zone's quote (O-02). */
    deliveryChargeOverridden: z.boolean(),
    deliveryZoneId: z.uuid().nullable(),
    weightGrams: z.int(),
    /** Advance the customer promised or sent (recorded intent; payments are B-35). */
    advancePaisa: paisa(),
    /** E.164; the shipping phone, used for duplicate detection (O-04). */
    customerPhone: z.string().nullable(),
    shippingAddress: shippingAddressSchema.nullable(),
    /** O-09: free labels (lower-cased) and who handles the order. */
    tags: z.array(z.string()),
    assigneeId: z.uuid().nullable(),
    businessDate: z.iso.date(),
    note: z.string(),
    cancelReason: z.string(),
    lineCount: z.int(),
    confirmedAt: z.iso.datetime().nullable(),
    shippedAt: z.iso.datetime().nullable(),
    deliveredAt: z.iso.datetime().nullable(),
    cancelledAt: z.iso.datetime().nullable(),
    version: z.int(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
    /** B-40: the phone's risk badge (order lists only). */
    risk: orderRiskSchema.nullable().optional(),
});
export const orderLineSchema = z.object({
    id: z.uuid(),
    lineNo: z.int(),
    variantId: z.uuid(),
    /** Product name and SKU when the order was made. */
    name: z.string(),
    sku: z.string(),
    qty: stockQtySchema,
    /** Decimals of the unit (quantities are in its smallest step). */
    unitPrecision: z.int(),
    /** Per whole unit (per kg for a kg product). */
    unitPricePaisa: paisa(),
    /** The line discount as a percentage (basis points), or null for an amount or none. */
    discountBp: z.int().nullable(),
    lineDiscountPaisa: paisa(),
    billDiscountSharePaisa: paisa(),
    taxRateBp: z.int(),
    taxPaisa: paisa(),
    totalPaisa: paisa(),
    /** The moving average when stock left (shipping); the catalog cost until then. */
    unitCostPaisa: paisa(),
    returnedQty: stockQtySchema,
});
export const orderTimelineEntrySchema = z.object({
    id: z.uuid(),
    kind: z.enum(ORDER_EVENT_KINDS),
    from: z.string().nullable(),
    to: z.string(),
    note: z.string(),
    actorId: z.uuid().nullable(),
    occurredAt: z.iso.datetime(),
});
/** Another open order for the same phone inside the duplicate window (O-04). */
export const orderDuplicateSchema = z.object({
    id: z.uuid(),
    number: z.string(),
    status: orderStatusSchema,
    totalPaisa: paisa(),
    createdAt: z.iso.datetime(),
});
export const orderDetailSchema = orderSchema.extend({
    lines: z.array(orderLineSchema),
    timeline: z.array(orderTimelineEntrySchema),
    duplicates: z.array(orderDuplicateSchema),
    /** O-07/O-08: goods that came back, and exchanges (each links its replacement order). */
    returns: z.array(z.object({
        id: z.uuid(),
        number: z.string(),
        kind: z.enum(["delivery_return", "customer_return", "exchange"]),
        totalPaisa: paisa(),
        exchangeOrderId: z.uuid().nullable(),
        createdAt: z.iso.datetime(),
    })),
    /** O-09 internal notes, oldest first (never shown to the customer). */
    notes: z.array(z.object({ id: z.uuid(), body: z.string(), authorId: z.uuid().nullable(), createdAt: z.iso.datetime() })),
});
export const orderListQuerySchema = z.object({
    status: orderStatusSchema.optional(),
    /** E.164 shipping phone. */
    phone: z
        .string()
        .regex(/^\+8801[3-9]\d{8}$/)
        .optional(),
    channel: orderChannelSchema.optional(),
    locationId: z.uuid().optional(),
    customerId: z.uuid().optional(),
    /** O-09 filters: a tag, the assignee (`none` = unassigned), business dates, and an order number prefix. */
    tag: z.string().trim().toLowerCase().min(1).max(40).optional(),
    assigneeId: z.union([z.uuid(), z.literal("none")]).optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    q: z.string().trim().min(1).max(40).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const orderListSchema = z.object({ data: z.array(orderSchema), nextCursor: z.uuid().nullable() });
/**
 * `POST /v1/orders`: a `draft` or `pending` order. Prices default to the variant's price at the location; totals
 * are always computed by the server (`computeBill`).
 */
export const createOrderRequestSchema = z
    .object({
    channel: z.enum(MANUAL_ORDER_CHANNELS),
    locationId: z.uuid(),
    customerId: z.uuid().nullable().default(null),
    status: z.enum(["draft", "pending"]).default("draft"),
    lines: orderLinesInputSchema,
    billDiscount: discountSchema.optional(),
    shippingAddress: shippingAddressInputSchema.nullable().default(null),
    weightGrams: z.int().min(0).max(1_000_000).default(0),
    /** Typed charge (overrides the zone's quote); left out → the zone's quote, or 0 without an address. */
    deliveryChargePaisa: paisa().min(0).optional(),
    advancePaisa: paisa().min(0).default(0),
    /**
     * Where the advance was received: with it, the advance is recorded as a payment for the order (B-35) and the
     * payment status follows; without it, the advance stays a note on the order (promised, not yet received).
     */
    advancePayment: z
        .object({
        accountId: z.uuid(),
        method: paymentMethodSchema,
        reference: z.string().trim().min(1).max(60).nullable().default(null),
    })
        .strict()
        .optional(),
    note: note.default(""),
    clientMutationId: z.uuid().optional(),
})
    .strict()
    .refine((r) => r.advancePayment === undefined || r.advancePaisa > 0, {
    message: "Enter the advance amount",
    path: ["advancePaisa"],
});
/**
 * `POST /v1/orders/{id}/status`: one step of the state machine. `returnedLines` is required for
 * `partially_delivered` (what came back); `returned` takes everything back.
 */
export const changeOrderStatusRequestSchema = z
    .object({
    to: orderStatusSchema,
    version,
    note: note.default(""),
    returnedLines: z
        .array(z.object({ lineId: z.uuid(), qty: positiveQty }).strict())
        .min(1)
        .max(MAX_ORDER_LINES)
        .optional(),
})
    .strict();
/** Outbox data for `sales.order.*`: the order plus what handlers need of each line. */
export const orderEventSchema = orderSchema.extend({
    from: orderStatusSchema.nullable(),
    lines: z.array(z.object({ id: z.uuid(), variantId: z.uuid(), qty: stockQtySchema, totalPaisa: paisa() })),
});
export const orderPaymentChangedEventSchema = z.object({
    orderId: z.uuid(),
    from: paymentStatusSchema,
    to: paymentStatusSchema,
    /** DG-06: what a library grant needs — the order's number, its shopper account and its lines. */
    number: z.string().default(""),
    shopperAccountId: z.uuid().nullable().default(null),
    lines: z.array(z.object({ id: z.uuid(), variantId: z.uuid() })).default([]),
});
// Endpoints ----------------------------------------------------------------------------------------------------------
const tag = { tag: "sales" };
export const listOrders = defineEndpoint({
    ...tag,
    name: "listOrders",
    method: "GET",
    path: "/v1/orders",
    access: { permission: "orders.view" },
    summary: "Orders at the caller's locations, newest first",
    query: orderListQuerySchema,
    status: 200,
    response: orderListSchema,
});
export const createOrder = defineEndpoint({
    ...tag,
    name: "createOrder",
    method: "POST",
    path: "/v1/orders",
    access: { permission: "orders.create" },
    summary: "Create an order (draft or pending); an advance with `advancePayment` is recorded as a payment",
    body: createOrderRequestSchema,
    idempotent: true,
    status: 201,
    response: orderDetailSchema,
    errors: [
        "LOCATION_INACTIVE",
        "VARIANT_NOT_SELLABLE",
        "INVALID_DISCOUNT",
        "ACCOUNT_ARCHIVED",
        "PLAN_LIMIT_REACHED",
    ],
    entitlement: "orders.monthly.max",
});
export const getOrder = defineEndpoint({
    ...tag,
    name: "getOrder",
    method: "GET",
    path: "/v1/orders/{id}",
    access: { permission: "orders.view" },
    summary: "One order with its lines and timeline",
    params: idParamsSchema,
    status: 200,
    response: orderDetailSchema,
});
export const changeOrderStatus = defineEndpoint({
    ...tag,
    name: "changeOrderStatus",
    method: "POST",
    path: "/v1/orders/{id}/status",
    access: { permission: "orders.manage" },
    summary: "Move an order one step (confirm, pack, ship, deliver, return, cancel); stock follows",
    params: idParamsSchema,
    body: changeOrderStatusRequestSchema,
    idempotent: true,
    status: 200,
    response: orderDetailSchema,
    errors: ["CONFLICT", "INVALID_TRANSITION", "INSUFFICIENT_STOCK", "OVER_RETURN"],
});
//# sourceMappingURL=orders.js.map